// frontend/src/pages/HomePage.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const HomePage = () => {
    const navigate = useNavigate();

    const [winningDraws, setWinningDraws] = useState([]);
    // New state to store ALL past declared winning draws for the "See More" modal
    const [allPastDeclaredWinners, setAllPastDeclaredWinners] = useState([]);
    // Modified state variables to separate active normal and VIP draws
    const [allActiveNormalDraws, setAllActiveNormalDraws] = useState([]);
    const [allActiveVIPDraws, setAllActiveVIPDraws] = useState([]);
    const [selectedDraw, setSelectedDraw] = useState(null);
    const [selectedNumbers, setSelectedNumbers] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState('eZ Cash');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [showConfirmPopup, setShowConfirmPopup] = useState(false);
    const [generatedReference, setGeneratedReference] = useState('');
    const [reservationCountdown, setReservationCountdown] = useState(600);
    const countdownIntervalRef = useRef(null);
    const [reservedBetDetails, setReservedBetDetails] = useState(null);

    // Stores the full response for locked numbers, including isVIP and numberRangeMax
    const [lockedDrawInfo, setLockedDrawInfo] = useState({ lockedNumbers: [], isVIP: false, numberRangeMax: 99 });

    const [showWinnerModal, setShowWinnerModal] = useState(false);
    const [currentWinnerDrawName, setCurrentWinnerDrawName] = useState('');
    const [currentWinners, setCurrentWinners] = useState([]);
    const [fetchingWinner, setFetchingWinner] = useState(false);
    // New state for "See All Winners" modal
    const [showAllWinnersPastDrawsModal, setShowAllWinnersPastDrawsModal] = useState(false);


    const [drawCountdowns, setDrawCountdowns] = useState({});

    // Ref for the betting section to scroll to it
    const bettingSectionRef = useRef(null);
    const [showHowItWorks, setShowHowItWorks] = useState(false); // State for "How It Works" accordion

    const userInfoString = localStorage.getItem('userInfo');
    const user = userInfoString ? JSON.parse(userInfoString) : null;

    // Custom Tailwind animations via CSS
    const customAnimations = `
        @keyframes pulse-slow {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
        }
        @keyframes fade-in-down {
            from { opacity: 0; transform: translateY(-20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in-up {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes zoom-in-out {
            0% { transform: scale(0.9); opacity: 0; }
            50% { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(1); }
        }
        @keyframes bounce-in {
            0% { transform: scale(0.8); opacity: 0; }
            60% { transform: scale(1.1); opacity: 1; }
            100% { transform: scale(1); }
        }
        @keyframes bounce-subtle {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-3px); }
        }
        @keyframes shine-effect {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
        }
        .animate-pulse-slow {
            animation: pulse-slow 3s infinite ease-in-out;
        }
        .animate-fade-in-down {
            animation: fade-in-down 0.8s ease-out forwards;
        }
        .animate-fade-in-up {
            animation: fade-in-up 0.8s ease-out forwards;
        }
        .animate-zoom-in-out {
            animation: zoom-in-out 0.5s ease-out forwards;
        }
        .animate-bounce-in {
            animation: bounce-in 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards;
        }
        .animate-bounce-subtle {
            animation: bounce-subtle 1.5s infinite ease-in-out;
        }
        .shine-button {
            background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.2) 20%, rgba(255,255,255,0.5) 60%, rgba(255,255,255,0) 100%);
            background-size: 200% auto;
            transition: background-position 0.5s ease-out;
        }
        .shine-button:hover {
            animation: shine-effect 1.5s infinite linear;
        }
    `;

    const fetchLockedNumbersForDraw = useCallback(async (drawId) => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/draws/${drawId}/locked-numbers`);
            const data = await res.json();
            if (res.ok) {
                // Update lockedDrawInfo with all relevant data
                setLockedDrawInfo({
                    lockedNumbers: data.lockedNumbers || [],
                    isVIP: data.isVIP || false,
                    numberRangeMax: data.numberRangeMax !== undefined ? data.numberRangeMax : 99,
                });
            } else {
                throw new Error(data.message || 'Failed to fetch locked numbers for draw.');
            }
        } catch (err) {
            toast.error(err.message || 'Error fetching locked numbers.');
            setLockedDrawInfo({ lockedNumbers: [], isVIP: false, numberRangeMax: 99 }); // Reset on error
        }
    }, []);

    const fetchAllDrawsAndCategorize = useCallback(async () => {
        try {
            const activeRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/draws/active`);
            const pastRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/draws/past`);

            const activeData = await activeRes.json();
            const pastData = await pastRes.json();

            if (!activeRes.ok) {
                throw new Error(activeData.message || 'Failed to fetch active draws');
            }
            if (!pastRes.ok) {
                throw new Error(pastData.message || 'Failed to fetch past draws');
            }

            // Ensure isVIP and numberRangeMax are included in the draw objects
            const processDraws = (draws) => draws.map(draw => ({
                ...draw,
                isVIP: draw.isVIP || false,
                numberRangeMax: draw.numberRangeMax !== undefined ? draw.numberRangeMax : 99,
            }));

            // Filter and sort for the latest 3 winning draws for the "Recent Winners" section
            const declaredWinners = processDraws(pastData).filter(d => d.status === 'declared' && typeof d.winningNumber === 'number');
            setAllPastDeclaredWinners(declaredWinners.sort((a, b) => new Date(b.deadline).getTime() - new Date(a.deadline).getTime())); // Store all sorted winners

            const latestWinningDraws = declaredWinners.slice(0, 3); // Get only the latest 3 for prominent display

            // Separate VIP and non-VIP draws among the latest 3 winning draws
            const vipWinningDraw = latestWinningDraws.find(draw => draw.isVIP);
            const nonVipWinningDraws = latestWinningDraws.filter(draw => !draw.isVIP);

            // Construct the display order: VIP (if exists) in middle, others around it
            let displayedWinningDraws = [];
            if (vipWinningDraw) {
                // If there's a VIP draw, place it in the middle (index 1)
                // Fill remaining spots with non-VIP draws
                displayedWinningDraws[1] = vipWinningDraw;
                let nonVipIndex = 0;
                if (nonVipWinningDraws[nonVipIndex]) {
                    displayedWinningDraws[0] = nonVipWinningDraws[nonVipIndex++];
                }
                if (nonVipWinningDraws[nonVipIndex]) {
                    // Place the third draw if available, either at index 2 or 0 if index 0 is empty
                    if (!displayedWinningDraws[0]) {
                        displayedWinningDraws[0] = nonVipWinningDraws[nonVipIndex];
                    } else {
                        displayedWinningDraws[2] = nonVipWinningDraws[nonVipIndex];
                    }
                }
                // Filter out any undefined slots if there are fewer than 3 draws
                displayedWinningDraws = displayedWinningDraws.filter(Boolean);

            } else {
                // If no VIP draw, just display the latest 3 non-VIP draws
                displayedWinningDraws = nonVipWinningDraws;
            }

            setWinningDraws(displayedWinningDraws); // Set the organized winning draws


            // Filter and categorize active draws for "Your Next Big Win Awaits!" section
            const activeDraws = processDraws(activeData).filter(d => d.status === 'active' && new Date(d.deadline) > new Date());

            const activeVIPDraws = activeDraws.filter(draw => draw.isVIP);
            const activeNormalDraws = activeDraws.filter(draw => !draw.isVIP);

            // Sort active draws by deadline (earliest first) within their categories
            const sortedVIPDraws = activeVIPDraws.sort((a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime());
            const sortedNormalDraws = activeNormalDraws.sort((a, b) => new Date(a.deadline).getTime() - new Date(b.deadline).getTime());

            // --- Custom Ordering for Active Draws (Closest Deadline in Middle) ---
            const arrangeForDisplay = (draws) => {
                if (draws.length < 3) {
                    return draws; // If less than 3, just return as is (fill left to right)
                }
                const [first, second, third, ...rest] = draws; // Destructure first 3
                return [second, first, third, ...rest]; // Place 1st (closest deadline) in middle (index 1)
            };

            setAllActiveVIPDraws(arrangeForDisplay(sortedVIPDraws));
            setAllActiveNormalDraws(arrangeForDisplay(sortedNormalDraws));

        } catch (err) {
            toast.error(err.message || 'Error fetching draws.');
            setError(err.message || 'Error fetching draws.');
        }
    }, []);

    useEffect(() => {
        fetchAllDrawsAndCategorize();

        return () => {
            if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
            }
        };
    }, [fetchAllDrawsAndCategorize]);


    useEffect(() => {
        // Combine active normal and VIP draws for lookup
        const allCurrentActiveDraws = [...allActiveNormalDraws, ...allActiveVIPDraws];

        if (selectedDraw) {
            // Find the most up-to-date version of the selected draw from all active draws
            const updatedSelectedDraw = allCurrentActiveDraws.find(d => d._id === selectedDraw._id);

            if (updatedSelectedDraw && updatedSelectedDraw.status === 'active') {
                // Ensure numberRangeMax is correctly set if the draw is a VIP draw
                const drawWithVIPInfo = {
                    ...updatedSelectedDraw,
                    isVIP: updatedSelectedDraw.isVIP || false,
                    numberRangeMax: updatedSelectedDraw.numberRangeMax !== undefined ? updatedSelectedDraw.numberRangeMax : 99,
                };
                setSelectedDraw(drawWithVIPInfo);
                // Also update lockedDrawInfo with the latest draw details to ensure correct number grid rendering
                setLockedDrawInfo(prev => ({
                    ...prev,
                    isVIP: drawWithVIPInfo.isVIP,
                    numberRangeMax: drawWithVIPInfo.numberRangeMax,
                }));
            } else if (selectedDraw.status === 'active' && (!updatedSelectedDraw || updatedSelectedDraw.status !== 'active')) {
                setSelectedDraw(null);
                setSelectedNumbers([]);
                setLockedDrawInfo({ lockedNumbers: [], isVIP: false, numberRangeMax: 99 }); // Reset locked info
                toast.warn(`The draw "${selectedDraw.name}" is no longer active for betting.`);
            }
        }
    }, [allActiveNormalDraws, allActiveVIPDraws, selectedDraw, selectedDraw?._id]); // Depend on both active draw lists


    useEffect(() => {
        const interval = setInterval(() => {
            setDrawCountdowns(prevCountdowns => {
                const newCountdowns = { ...prevCountdowns };
                let needsUpdate = false;
                // Iterate through both active normal and VIP draws
                [...allActiveNormalDraws, ...allActiveVIPDraws].forEach(draw => {
                    if (draw.status === 'active') {
                        const timeLeft = Math.max(0, Math.floor((new Date(draw.deadline).getTime() - Date.now()) / 1000));
                        if (newCountdowns[draw._id] !== timeLeft) {
                            newCountdowns[draw._id] = timeLeft;
                            needsUpdate = true;
                        }
                        if (timeLeft === 0 && draw.status === 'active') {
                            toast.info(`Draw "${draw.name}" has just ended! Numbers are no longer open for betting.`);
                            fetchAllDrawsAndCategorize(); // Re-fetch all draws to update status
                        }
                    }
                });
                return needsUpdate ? newCountdowns : prevCountdowns;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [allActiveNormalDraws, allActiveVIPDraws, fetchAllDrawsAndCategorize]); // Depend on both active draw lists


    useEffect(() => {
        if (showConfirmPopup && reservationCountdown > 0) {
            countdownIntervalRef.current = setInterval(() => {
                setReservationCountdown(prev => prev - 1);
            }, 1000);
        } else if (reservationCountdown === 0 && showConfirmPopup) {
            clearInterval(countdownIntervalRef.current);
            
        }
        return () => clearInterval(countdownIntervalRef.current);
    }, [showConfirmPopup, reservationCountdown]);


    const formatTime = (seconds) => {
        if (seconds < 0) return "00:00:00";
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const handleNumberSelect = (num) => {
        if (showConfirmPopup) {
            toast.info('Please complete or cancel your current bet reservation first.');
            return;
        }
        // Use lockedDrawInfo.lockedNumbers
        if (lockedDrawInfo.lockedNumbers.includes(num)) {
            toast.warn('This number is already taken. Please select another.');
            return;
        }

        setSelectedNumbers(prev =>
            prev.includes(num)
                ? prev.filter(n => n !== num)
                : [...prev, num].sort((a, b) => a - b)
        );
    };

    const handleDrawSelect = (draw) => {
        if (selectedDraw?._id !== draw._id) {
            // Ensure isVIP and numberRangeMax are correctly set when a draw is selected
            const drawWithVIPInfo = {
                ...draw,
                isVIP: draw.isVIP || false,
                numberRangeMax: draw.numberRangeMax !== undefined ? draw.numberRangeMax : 99,
            };
            setSelectedDraw(drawWithVIPInfo);
            setSelectedNumbers([]);
            setLockedDrawInfo({ lockedNumbers: [], isVIP: drawWithVIPInfo.isVIP, numberRangeMax: drawWithVIPInfo.numberRangeMax }); // Initialize locked info for new draw
            if (draw.status === 'active' && draw._id) {
                fetchLockedNumbersForDraw(draw._id);
            }
        }
        if (bettingSectionRef.current) {
            bettingSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const generateUniqueReference = () => {
        const timestamp = Date.now().toString(36);
        const randomStr = Math.random().toString(36).substring(2, 8);
        return `BET-${timestamp}-${randomStr}`.toUpperCase();
    };

    const handleConfirmBet = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!selectedDraw) {
            toast.error('Please select a draw to place your bet.');
            return;
        }
        if (selectedDraw.status !== 'active' || (drawCountdowns[selectedDraw._id] !== undefined && drawCountdowns[selectedDraw._id] <= 0)) {
            toast.error('This draw is no longer active for betting. Please select another draw.');
            setSelectedDraw(null);
            setSelectedNumbers([]);
            setLockedDrawInfo({ lockedNumbers: [], isVIP: false, numberRangeMax: 99 }); // Reset locked info
            return;
        }


        if (selectedNumbers.length === 0) {
            toast.error('Please select at least one number.');
            return;
        }
        // Validate selected numbers against the draw's number range
        const maxNumber = selectedDraw.numberRangeMax;
        const invalidNumbers = selectedNumbers.filter(num => num < 0 || num > maxNumber);
        if (invalidNumbers.length > 0) {
            toast.error(`Selected numbers ${invalidNumbers.join(', ')} are outside the valid range (0-${maxNumber}) for this draw. Please re-select.`);
            setSelectedNumbers([]);
            return;
        }


        if (!user || !user.token) {
            toast.error('You must be logged in to place a bet.');
            return;
        }

        // Use lockedDrawInfo.lockedNumbers
        const alreadyLocked = selectedNumbers.filter(num => lockedDrawInfo.lockedNumbers.includes(num));
        if (alreadyLocked.length > 0) {
            toast.error(`Some of your selected numbers (${alreadyLocked.join(', ')}) are now taken. Please re-select your numbers.`);
            fetchLockedNumbersForDraw(selectedDraw._id);
            setSelectedNumbers([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        const refNumber = generateUniqueReference();
        setGeneratedReference(refNumber);
        const amountToPay = selectedNumbers.length * selectedDraw.entryCost;

        setReservedBetDetails({
            drawId: selectedDraw._id,
            selectedNumbers,
            amount: amountToPay,
            paymentMethod,
            referenceNumber: refNumber,
            drawName: selectedDraw.name
        });

        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/bets/reserve`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`,
                },
                body: JSON.stringify({
                    drawId: selectedDraw._id,
                    selectedNumbers,
                    referenceNumber: refNumber,
                    reservationExpiry: Date.now() + 600000,
                    paymentMethod,
                }),
            });

            const data = await res.json();

            if (!res.ok) {
                if (data.message && data.message.includes('already reserved')) {
                    toast.error('Some of your selected numbers were just reserved by another user. Please try again with different numbers.');
                    fetchLockedNumbersForDraw(selectedDraw._id);
                    setSelectedNumbers([]);
                } else {
                    throw new Error(data.message || 'Failed to reserve numbers.');
                }
                return;
            }

            toast.info('Numbers reserved for 10 minutes. Please complete payment within the time limit.');
            setShowConfirmPopup(true);
            setReservationCountdown(600);
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = setInterval(() => {
                setReservationCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(countdownIntervalRef.current);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

        } catch (err) {
            toast.error(err.message || 'Failed to initiate bet reservation. Please try again.');
            setError(err.message || 'Failed to initiate bet reservation.');
            setGeneratedReference('');
            setReservedBetDetails(null);
        } finally {
            setLoading(false);
        }
    };

    const handleProceedToPayment = () => {
        clearInterval(countdownIntervalRef.current);
        setShowConfirmPopup(false);
    
        if (reservedBetDetails) {
            navigate('/deposit', {
                state: {
                    amount: reservedBetDetails.amount,
                    transactionId: reservedBetDetails.referenceNumber,
                    paymentMethod: reservedBetDetails.paymentMethod,
                }
            });
    
            // Force a reload after short delay to trigger proper rendering
            setTimeout(() => {
                window.location.reload();
            }, 100);
        }
    };
    
    
    

    const handleCancelReservation = async () => {
        clearInterval(countdownIntervalRef.current);
        setShowConfirmPopup(false);
        setReservationCountdown(600);
        setLoading(true);

        if (!user || !user.token || !reservedBetDetails) {
            toast.error('Cannot cancel reservation: user not logged in or no active reservation.');
            setLoading(false);
            return;
        }

        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/bets/cancel-reservation`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`,
                },
                body: JSON.stringify({
                    referenceNumber: reservedBetDetails.referenceNumber,
                    drawId: reservedBetDetails.drawId,
                    selectedNumbers: reservedBetDetails.selectedNumbers
                }),
            });

            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.message || 'Failed to cancel reservation on backend.');
            }
            toast.success(data.message || 'Bet reservation cancelled. Numbers are now available.');
            setSelectedNumbers([]);
            setGeneratedReference('');
            setReservedBetDetails(null);
            if (selectedDraw?._id) {
                fetchLockedNumbersForDraw(selectedDraw._id);
            }
        } catch (err) {
            toast.error(err.message || 'Error cancelling reservation.');
            setError(err.message || 'Error cancelling reservation.');
        } finally {
            setLoading(false);
        }
    };

    

    const handleShowWinnerModal = useCallback(async (draw) => {
        setFetchingWinner(true);
        setCurrentWinnerDrawName(draw.name);
        setCurrentWinners([]); // Clear previous winners

        try {
            // Ensure draw._id is correctly passed
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/draws/${draw._id}/winner-details`);
            const data = await response.json();

            if (!response.ok) {
                // If backend sends a 404 or other error, it should contain a message
                throw new Error(data.message || 'Failed to fetch winner details.');
            }

            // CORRECTED: Access the 'winners' array from the returned data object
            if (data.winners && data.winners.length > 0) {
                setCurrentWinners(data.winners); // Set the actual array of winner objects
            } else {
                toast.info('No winners found for this draw yet, or data not available.');
                setCurrentWinners([]); // Ensure it's an empty array if no winners
            }
            setShowWinnerModal(true);
        } catch (err) {
            toast.error(err.message || 'Error fetching winner details.');
            setCurrentWinners([]); // Ensure it's empty on error
        } finally {
            setFetchingWinner(false);
        }
    }, []);

    // Calculate dynamic styles for "Hot Draw" or "Last Chance"
    const getDrawCardHighlight = (draw) => {
        if (draw.status === 'active' && drawCountdowns[draw._id] !== undefined) {
            const timeLeft = drawCountdowns[draw._id];
            // For VIP draws, the total possible numbers will be (draw.numberRangeMax + 1)
            const totalPossibleNumbers = draw.isVIP ? (draw.numberRangeMax + 1) : 100;
            const isHot = (draw.lockedNumbers?.length || 0) > (totalPossibleNumbers * 0.3); // if more than 30% numbers are locked
            // Simulate 'last chance' if less than 1 hour (3600 seconds)
            const isLastChance = timeLeft > 0 && timeLeft <= 3600;

            if (isLastChance) {
                return 'border-red-500 ring-4 ring-red-200 shadow-lg animate-pulse-fast bg-red-50';
            }
            if (isHot) {
                return 'border-purple-500 ring-4 ring-purple-200 shadow-lg bg-purple-50';
            }
        }
        return '';
    };

    // Helper to render draw cards, used for both VIP and Normal active draws
    const renderDrawCards = (draws) => {
        return draws.map(draw => (
            <div
                key={draw._id}
                className={`p-6 border rounded-xl shadow-lg transition duration-300 transform hover:scale-105 hover:shadow-2xl
                    ${selectedDraw?._id === draw._id
                        ? 'border-blue-600 ring-4 ring-blue-300 bg-blue-50'
                        : draw.isVIP // Differentiate border for VIP draws in 'Next Big Win' section
                            ? 'border-purple-400 bg-white hover:border-purple-500'
                            : 'border-gray-300 bg-white hover:border-blue-400'
                    }
                    cursor-pointer
                    ${getDrawCardHighlight(draw)}
                `}
                onClick={() => handleDrawSelect(draw)}
            >
                <h3 className="text-2xl font-bold text-blue-800 mb-2">
                    {draw.isVIP && <span className="text-yellow-500 mr-2">🌟 VIP 🌟</span>}
                    {draw.name}
                </h3>
                <p className="text-sm text-gray-600 mb-1">Deadline: {new Date(draw.deadline).toLocaleString()}</p>
                <p className="text-md text-gray-700">Entry Cost per Number: <span className="font-semibold text-green-600">Rs. {draw.entryCost}</span></p>
                <p className="text-md text-gray-700">
                    Prize Pool: <span className="font-semibold text-purple-700">
                        {draw.prizeValue}{draw.prizePoolType === 'percentage' ? '%' : ' Rs.'}
                        {draw.prizePoolType === 'percentage' ? ' (Growing!)' : ''}
                    </span>
                </p>
                <p className="text-md text-gray-700">
                    Numbers: <span className="font-semibold text-orange-600">0 - {draw.numberRangeMax}</span>
                </p>
                <p className="text-md text-gray-700 font-semibold mb-3">Status: <span className="text-green-600">{draw.status.charAt(0).toUpperCase() + draw.status.slice(1)}</span></p>

                {draw.status === 'active' && drawCountdowns[draw._id] !== undefined && (
                    <div className="mt-3 p-3 bg-blue-100 rounded-lg text-center border-2 border-blue-300 shadow-inner">
                        <p className="text-sm text-blue-800 font-semibold mb-1">Time left to Bet:</p>
                        <p className="text-3xl font-extrabold text-blue-900 animate-pulse-fast">
                            {formatTime(drawCountdowns[draw._id])}
                        </p>
                    </div>
                )}
                {draw.status === 'active' && draw.lockedNumbers && Array.isArray(draw.lockedNumbers) && (
                    <p className="text-sm text-gray-700 mt-2 font-semibold">
                        🔥 <span className="text-orange-500">{draw.lockedNumbers.length}</span> numbers taken! Join the action!
                    </p>
                )}
                {selectedDraw?._id === draw._id && (
                    <p className="text-sm text-blue-500 font-semibold mt-2 animate-bounce-subtle">Selected for betting below!</p>
                )}
            </div>
        ));
    };


    return (
        <div className="container mx-auto p-4 mt-8 font-inter">
            {/* Inject custom CSS animations */}
            <style>{customAnimations}</style>

            <h1 className="text-4xl font-extrabold text-center mb-6 text-gray-900 animate-fade-in-down">
                Unleash Your Luck at BetMania!
            </h1>
            <p className="text-xl text-center text-gray-600 mb-12 animate-fade-in-up">
                Pick your lucky numbers, place your bets, and win big! The thrill awaits!
            </p>

            {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 shadow-md animate-fade-in-down">{error}</div>}
            {success && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4 shadow-md animate-fade-in-down">{success}</div>}

            {/* NEW: Winning Draws Section - Prominently displayed at the top */}
            {allPastDeclaredWinners.length > 0 && ( // Check against allPastDeclaredWinners to show section
                <div className="bg-gradient-to-br from-purple-700 to-indigo-800 p-8 rounded-xl shadow-2xl mb-12 transform -rotate-1 scale-102 transition-all duration-500 hover:rotate-0 hover:scale-100 animate-fade-in-up">
                    <h2 className="text-4xl font-extrabold text-white text-center mb-6 tracking-wide animate-pulse-slow">
                        🌟 Recent Winners! 🌟
                    </h2>
                    <p className="text-xl text-purple-200 text-center mb-8 italic">
                        Could you be next to see your name here? There's always a new chance!
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {winningDraws.map(draw => ( // Only map the latest 3 here
                            <div
                                key={draw._id}
                                className={`
                                    bg-white p-6 rounded-lg shadow-xl border border-yellow-400
                                    ${draw.isVIP ? 'transform scale-105 rotate-3 border-purple-600 ring-4 ring-purple-300' : 'transform rotate-2'}
                                    hover:rotate-0 hover:scale-105 transition-all duration-300 text-center overflow-hidden relative
                                `}
                            >
                                {/* Shine effect for winning numbers */}
                                <div className="absolute inset-0 bg-gradient-to-r from-yellow-100 via-white to-yellow-100 opacity-20 transform -skew-y-12 animate-shine-effect pointer-events-none"></div>

                                <h3 className="text-2xl font-bold text-blue-700 mb-2">
                                    {draw.isVIP && <span className="text-purple-500 mr-2">💎 VIP Win! 💎</span>}
                                    {draw.name}
                                </h3>
                                <p className="text-sm text-gray-600 mb-3">Declared: {new Date(draw.deadline).toLocaleDateString()}</p>
                                <div className="p-3 bg-gradient-to-r from-yellow-300 to-amber-400 rounded-md shadow-lg mb-4 transform scale-105 animate-bounce-subtle">
                                    <p className="text-xl font-extrabold text-white animate-pulse">WINNING NUMBER</p>
                                    <p className="text-5xl font-extrabold text-purple-800 drop-shadow-lg leading-none">
                                        {draw.winningNumber < 10 ? `0${draw.winningNumber}` : draw.winningNumber}
                                    </p>
                                </div>
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleShowWinnerModal(draw); }}
                                    className="mt-3 bg-blue-600 text-white font-bold py-2 px-5 rounded-full shadow-lg hover:bg-blue-700 hover:scale-105 transition-transform duration-300 transform -translate-y-1"
                                    disabled={fetchingWinner}
                                >
                                    {fetchingWinner ? 'Finding Lucky Person...' : 'See The Lucky Person!'}
                                </button>
                            </div>
                        ))}
                    </div>
                    {allPastDeclaredWinners.length > 3 && ( // Show "See More" only if there are more than 3 winners
                        <div className="text-center mt-8">
                            <button
                                onClick={() => setShowAllWinnersPastDrawsModal(true)}
                                className="bg-white text-purple-700 font-bold py-3 px-8 rounded-full shadow-lg hover:bg-purple-50 hover:scale-105 transition duration-300 border-2 border-purple-700"
                            >
                                See All Past Winners
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* How It Works Section */}
            <div className="bg-gradient-to-r from-green-500 to-teal-600 text-white p-6 rounded-lg shadow-xl mb-12 animate-fade-in-up">
                <h2 className="text-3xl font-bold text-center mb-4 cursor-pointer flex justify-center items-center" onClick={() => setShowHowItWorks(!showHowItWorks)}>
                    How It Works
                    <span className="ml-3 text-2xl transform transition-transform duration-300">
                        {showHowItWorks ? '▲' : '▼'}
                    </span>
                </h2>
                {showHowItWorks && (
                    <div className="mt-6 space-y-4 text-lg animate-fade-in-down">
                    <div className="flex items-start">
                      <span className="text-yellow-300 text-2xl mr-3">1️⃣</span>
                      <p>Browse through our exciting draws. Each draw has unique numbers, costs, and prize pools.</p>
                    </div>
                    <div className="flex items-start">
                      <span className="text-yellow-300 text-2xl mr-3">2️⃣</span>
                      <p>
                        <span className="font-bold text-black-500">
                          Pay close attention to the draw description — it holds the key to knowing exactly which numbers to bet on.
                        </span>
                      </p>
                    </div>
                    <div className="flex items-start">
                      <span className="text-yellow-300 text-2xl mr-3">2️⃣</span>
                      <p>Select your lucky numbers. Once reserved, you have 10 minutes to confirm your bet.</p>
                    </div>
                    <div className="flex items-start">
                      <span className="text-yellow-300 text-2xl mr-3">3️⃣</span>
                      <p>Proceed to payment! Use your unique reference number to ensure your bet is confirmed.</p>
                    </div>
                    <div className="flex items-start">
                      <span className="text-yellow-300 text-2xl mr-3">4️⃣</span>
                      <p>Wait for the draw! If your number matches the winning number, you win big!</p>
                    </div>
                  </div>
                  
                )}
            </div>


            {/* Your Next Big Win Awaits! Section - Only Active Draws */}
            {(allActiveNormalDraws.length > 0 || allActiveVIPDraws.length > 0) && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8 animate-fade-in-up">
                    <h2 className="text-3xl font-bold text-gray-700 mb-6 text-center">Your Next Big Win Awaits!</h2>

                    {/* VIP Active Draws Section */}
                    {allActiveVIPDraws.length > 0 && (
                        <div className="mb-10 p-6 rounded-xl shadow-inner bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300 animate-fade-in-down">
                            <h3 className="text-2xl font-extrabold text-purple-800 text-center mb-6 flex items-center justify-center">
                                <span className="text-4xl mr-2">👑</span> Exclusive VIP Draws! <span className="text-4xl ml-2">👑</span>
                            </h3>
                            <p className="text-center text-lg text-gray-700 mb-6">
                                Limited numbers, higher chances! Dive into our special VIP draws.
                            </p>
                            <div className={`grid gap-8 ${allActiveVIPDraws.length === 1 ? 'grid-cols-1 md:grid-cols-1 lg:grid-cols-1 justify-items-center' : allActiveVIPDraws.length === 2 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2 justify-center' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                                {renderDrawCards(allActiveVIPDraws, allActiveVIPDraws.length === 1)}
                            </div>
                        </div>
                    )}

                    {/* Normal Active Draws Section */}
                    
                    {/* Normal Active Draws Section */}
                    {allActiveNormalDraws.length > 0 && (
                        <div className="mt-8 p-6 rounded-xl shadow-inner bg-blue-50 border-2 border-blue-200 animate-fade-in-down">
                            <h3 className="text-2xl font-extrabold text-blue-800 text-center mb-6 flex items-center justify-center">
                                <span className="text-4xl mr-2">⭐</span> Standard Draws <span className="text-4xl ml-2">⭐</span>
                            </h3>
                            <p className="text-center text-lg text-gray-700 mb-6">
                                Classic draws with numbers from 0-99. Your daily chance to win!
                            </p>
                            <div className={`grid gap-8 ${allActiveNormalDraws.length === 1 ? 'grid-cols-1 md:grid-cols-1 lg:grid-cols-1 justify-items-center' : allActiveNormalDraws.length === 2 ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2 justify-center' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
                                {renderDrawCards(allActiveNormalDraws, allActiveNormalDraws.length === 1)}
                            </div>
                        </div>
                    )}
                    {(allActiveNormalDraws.length === 0 && allActiveVIPDraws.length === 0) && (
                        <p className="text-gray-600 text-center text-lg py-8">No active draws available at the moment. Please check back later for new opportunities!</p>
                    )}
                </div>
            )}

            {selectedDraw && selectedDraw.status === 'active' && ( // Only show betting section for active draws
                <div ref={bettingSectionRef} className="bg-white p-6 rounded-lg shadow-md mt-8 animate-fade-in-up border-t-4 border-blue-500 pb-32"> {/* Increased padding-bottom to ensure button visibility above sticky footer */}
                    <h2 className="text-3xl font-bold text-gray-700 mb-6 text-center">Place Your Bet for: <span className="text-blue-700">{selectedDraw.name}</span></h2>
                    <p className="text-center text-lg text-gray-600 mb-8">
                        Choose your lucky numbers from 0 to {selectedDraw.numberRangeMax}. Each selection brings you closer to victory!
                    </p>
                    <form onSubmit={handleConfirmBet}>
                        <div className="mb-8">
                            <label className="block text-gray-700 text-sm font-bold mb-3">
                                Select Your Lucky Numbers (0-{selectedDraw.numberRangeMax}):
                                <span className="text-gray-500 text-xs ml-2">(Click to select/deselect)</span>
                            </label>
                            <div className="grid grid-cols-10 sm:grid-cols-12 md:grid-cols-15 lg:grid-cols-20 gap-2 p-4 bg-gray-50 border rounded-xl max-h-80 overflow-y-auto shadow-inner">
                                {Array.from({ length: selectedDraw.numberRangeMax + 1 }, (_, i) => i).map(num => ( // Dynamically generate numbers
                                    <button
                                        key={num}
                                        type="button"
                                        className={`w-full py-2 px-1 text-sm sm:text-base font-bold rounded-lg transition duration-200 transform hover:scale-105 hover:shadow-lg
                                            ${selectedNumbers.includes(num)
                                                ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg border-2 border-blue-800 animate-bounce-subtle'
                                                : lockedDrawInfo.lockedNumbers.includes(num) // Use lockedDrawInfo
                                                    ? 'bg-red-500 text-white cursor-not-allowed opacity-70 border border-red-600'
                                                    : 'bg-gray-200 text-gray-800 hover:bg-gray-300 border border-gray-300'
                                            }
                                            ${showConfirmPopup || lockedDrawInfo.lockedNumbers.includes(num) ? 'opacity-50 cursor-not-allowed' : ''} // Use lockedDrawInfo
                                            `}
                                        onClick={() => handleNumberSelect(num)}
                                        disabled={showConfirmPopup || lockedDrawInfo.lockedNumbers.includes(num)} // Use lockedDrawInfo
                                    >
                                        {num < 10 && (selectedDraw.numberRangeMax >= 10 || selectedDraw.numberRangeMax === 99) ? `0${num}` : num} {/* Conditional formatting for 0-9 */}
                                    </button>
                                ))}
                            </div>
                            <p className="mt-4 text-gray-700 text-lg">
                                Your Chosen Numbers: <span className="font-extrabold text-blue-700">{selectedNumbers.length > 0 ? selectedNumbers.join(', ') : 'None selected yet'}</span>
                            </p>
                            <p className="text-gray-700 text-2xl font-bold mt-4">
                                Total Cost for Your Bet: <span className="text-green-700">Rs. {selectedNumbers.length * selectedDraw.entryCost}</span>
                            </p>
                        </div>

                        <div className="mb-8">
                            <label className="block text-gray-700 text-sm font-bold mb-3" htmlFor="paymentMethod">
                                Secure Your Bet - Select Payment Method:
                            </label>
                            <select
                                id="paymentMethod"
                                className="shadow border rounded-lg w-full py-3 px-4 text-gray-700 leading-tight focus:outline-none focus:ring-4 focus:ring-blue-500 focus:shadow-outline text-lg transition duration-200"
                                value={paymentMethod}
                                onChange={(e) => setPaymentMethod(e.target.value)}
                                required
                                disabled={showConfirmPopup}
                            >
                                <option value="eZ Cash">eZ Cash</option>
                                
                                
                            </select>
                        </div>

                        <button
                            type="submit"
                            className={`
                                py-4 px-8 rounded-full focus:outline-none focus:ring-4 w-full text-2xl transition duration-300 transform hover:scale-105
                                ${loading || !selectedDraw || showConfirmPopup || selectedNumbers.length === 0
                                    ? 'bg-gray-500 text-white cursor-not-allowed shadow-none' // Disabled style: darker gray
                                    : 'bg-gradient-to-r from-green-600 to-green-800 hover:from-green-700 hover:to-green-900 text-white font-extrabold shadow-2xl focus:ring-green-300' // Enabled style: more vibrant green
                                }
                            `}
                            disabled={loading || selectedNumbers.length === 0 || !selectedDraw || showConfirmPopup}
                        >
                            {loading ? 'Confirming Bet...' : `Place Your Bet Now! (Rs. ${selectedNumbers.length * selectedDraw.entryCost})`}
                        </button>
                    </form>
                </div>
            )}

            {/* Sticky "Place Bet Now" button */}
            {selectedDraw && selectedDraw.status === 'active' && (
                <div className="fixed bottom-0 left-0 right-0 bg-blue-700 text-white p-4 shadow-2xl z-40 text-center animate-fade-in-up">
                    <button
                        onClick={() => {
                            if (bettingSectionRef.current) {
                                bettingSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                        }}
                        className="bg-yellow-400 text-blue-900 font-extrabold py-3 px-8 rounded-full text-xl shadow-lg hover:bg-yellow-300 transform hover:scale-105 transition duration-300 animate-pulse"
                    >
                        ⚡️ Bet Now on {selectedDraw.name}! ⚡️
                    </button>
                </div>
            )}


            {showConfirmPopup && reservedBetDetails && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md relative transform scale-100 animate-zoom-in-out border-t-8 border-indigo-600">
                        <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center text-indigo-700">🚀 Your Bet is Reserved! 🚀</h2>
                        <button
                            onClick={handleCancelReservation}
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-3xl font-light"
                            title="Cancel Reservation"
                        >
                            &times;
                        </button>

                        <div className="space-y-4 mb-8 text-gray-700 text-lg">
                            <p className="flex justify-between">
                                <span className="font-semibold text-blue-700">Draw:</span> <span className="font-bold text-gray-800">{reservedBetDetails.drawName}</span>
                            </p>
                            <p className="flex justify-between">
                                <span className="font-semibold text-blue-700">Your Numbers:</span> <span className="font-bold text-purple-700">{reservedBetDetails.selectedNumbers.join(', ')}</span>
                            </p>
                            <p className="flex justify-between text-2xl font-bold">
                                <span className="font-semibold text-blue-700">Amount Due:</span> <span className="text-green-700">Rs. {reservedBetDetails.amount}</span>
                            </p>
                            <div className="p-3 bg-yellow-100 rounded-md border border-yellow-300 text-center font-mono break-all mt-4 animate-bounce-subtle">
                                <span className="font-semibold text-yellow-800 text-lg">Your Unique Reference No:</span> <strong className="text-yellow-900 text-2xl block mt-1 select-all">{generatedReference}</strong>
                                <p className="text-xs text-gray-600 mt-2">Copy this number for your payment!</p>
                            </div>
                            <p className="text-3xl font-extrabold text-red-600 text-center mt-6 animate-pulse">
                                Time Left: {formatTime(reservationCountdown)}
                            </p>
                            <p className="text-sm text-gray-500 text-center italic">
                                Complete your payment using the provided reference number within this time limit to confirm your bet!
                            </p>
                        </div>

                        <div className="flex flex-col space-y-4">
                            <button
                                onClick={handleProceedToPayment}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-md transition duration-300 ease-in-out shadow-lg text-xl transform hover:scale-105"
                            >
                                Proceed to Payment Now!
                            </button>
                            <button
                                onClick={handleCancelReservation}
                                className="bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-6 rounded-md transition duration-300 ease-in-out shadow-md text-xl"
                            >
                                Cancel Bet Reservation
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Winner Modal */}
            {showWinnerModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md relative text-center transform scale-100 animate-bounce-in border-t-8 border-yellow-500">
                        <h2 className="text-3xl font-bold text-gray-800 mb-4 text-purple-700">🏆 Lucky Winner for <span className="text-blue-600">{currentWinnerDrawName}</span> 🏆</h2>
                        <button
                            onClick={() => setShowWinnerModal(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-3xl font-light"
                            title="Close"
                        >
                            &times;
                        </button>

                        {fetchingWinner ? (
                            <p className="text-lg text-blue-600 animate-pulse mt-4">Finding the luckiest person...</p>
                        ) : currentWinners.length > 0 ? ( // Now checking if currentWinners (which is an array) has length > 0
                            <div className="space-y-3 mt-4">
                                {currentWinners.map((winner, index) => (
                                    // CORRECTED: Access winner.fullName from the winner object
                                    <p key={index} className="text-4xl font-extrabold text-green-700 animate-fade-in animate-bounce-subtle">
                                        {winner.fullName}
                                    </p>
                                ))}
                                <p className="text-lg text-gray-600 mt-4">Congratulations to our lucky winner(s)! Will you be next to grab the prize?</p>
                            </div>
                        ) : (
                            <p className="text-lg text-gray-600 mt-4">No winner details available at this time. Perhaps no one bet on the winning number!</p>
                        )}
                        <button
                            onClick={() => setShowWinnerModal(false)}
                            className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-md transition duration-300 shadow-lg text-xl transform hover:scale-105"
                        >
                            Awesome!
                        </button>
                    </div>
                </div>
            )}

            {/* See All Past Winners Modal */}
            {showAllWinnersPastDrawsModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-2xl relative transform scale-100 animate-zoom-in-out border-t-8 border-indigo-600 max-h-[90vh] overflow-y-auto">
                        <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center text-indigo-700">All Past Winners!</h2>
                        <button
                            onClick={() => setShowAllWinnersPastDrawsModal(false)}
                            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 text-3xl font-light"
                            title="Close"
                        >
                            &times;
                        </button>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {allPastDeclaredWinners.length > 0 ? (
                                allPastDeclaredWinners.map(draw => (
                                    <div
                                        key={draw._id}
                                        className={`p-4 border rounded-lg shadow-md text-center
                                            ${draw.isVIP ? 'bg-purple-50 border-purple-400' : 'bg-blue-50 border-blue-200'}
                                        `}
                                    >
                                        <h3 className="text-xl font-bold text-gray-800 mb-1">
                                            {draw.isVIP && <span className="text-purple-600 text-sm mr-1">💎</span>}
                                            {draw.name}
                                        </h3>
                                        <p className="text-sm text-gray-600">Winning No: <span className="font-semibold text-green-700">{draw.winningNumber < 10 ? `0${draw.winningNumber}` : draw.winningNumber}</span></p>
                                        <p className="text-xs text-gray-500">Declared: {new Date(draw.deadline).toLocaleDateString()}</p>
                                    </div>
                                ))
                            ) : (
                                <p className="text-lg text-gray-600 col-span-full text-center">No past winners to display yet.</p>
                            )}
                        </div>
                        <div className="text-center mt-6">
                            <button
                                onClick={() => setShowAllWinnersPastDrawsModal(false)}
                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-md transition duration-300 shadow-lg text-xl"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HomePage;
